import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '~/modules/common/components';
import MoveTimeDialogContent from './MoveTimeDialogContent';
import { useDialogStyles } from './styles';
import { useMoveTimeDialogHandlers } from './hooks';
import { MOVE_TIME_STEP_INDICES } from './constants';

const MoveTimeDialog = ({ open, onClose, sourceProject, sourceTask }) => {
  const classes = useDialogStyles();
  const {
    activeStep,
    isNextDisabled,
    isSubmitting,
    moveTimeData,
    setMoveTimeData,
    handleClose,
    handleBack,
    handleNext,
    handleBatchComplete,
    handleCancelBatch,
    handleMoveMoreTimeEntries,
    timeEntrySummary,
    batchState,
    batchResults
  } = useMoveTimeDialogHandlers({ onClose, sourceProject, sourceTask });

  const cancelButton = (
    <Button
      color="default"
      variant="text"
      onClick={handleClose}
      data-qe-id="MoveTimeDialogCancelButton"
    >
      <FormattedMessage id="button.cancel" />
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-qe-id="MoveTimeDialog"
      aria-labelledby="move-time-dialog-title"
    >
      <DialogTitle id="move-time-dialog-title">
        <FormattedMessage id="moveTimeDialog.title" />
      </DialogTitle>

      <DialogContent className={classes.content}>
        <MoveTimeDialogContent
          activeStep={activeStep}
          moveTimeData={moveTimeData}
          setMoveTimeData={setMoveTimeData}
          timeEntrySummary={timeEntrySummary}
          batchState={batchState}
          batchResults={batchResults}
          onBatchComplete={handleBatchComplete}
          initialProject={sourceProject}
          initialTask={sourceTask}
        />
      </DialogContent>

      <DialogActions className={classes.actions}>
        {activeStep === MOVE_TIME_STEP_INDICES.PROGRESS ? (
          <Button
            color="default"
            variant="text"
            onClick={handleCancelBatch}
            data-qe-id="MoveTimeDialogCancelBatchButton"
          >
            <FormattedMessage id="button.cancel" />
          </Button>
        ) : (
          <>
            {activeStep === MOVE_TIME_STEP_INDICES.RESULT ? (
              <>
                <Button
                  onClick={handleMoveMoreTimeEntries}
                  color="primary"
                  data-qe-id="MoveTimeDialogMoveMoreButton"
                >
                  <FormattedMessage id="moveTimeDialog.moveMoreTimeEntries" />
                </Button>
                <Button
                  onClick={handleClose}
                  color="primary"
                  variant="contained"
                  data-qe-id="MoveTimeDialogOkButton"
                >
                  <FormattedMessage id="button.ok" />
                </Button>
              </>
            ) : (
              <>
                {activeStep === MOVE_TIME_STEP_INDICES.DESTINATION ? (
                  <>
                    <Button
                      color="default"
                      variant="text"
                      onClick={handleClose}
                      data-qe-id="MoveTimeDialogCancelButton"
                      disabled={isSubmitting}
                    >
                      <FormattedMessage id="button.cancel" />
                    </Button>
                    <Button
                      color="primary"
                      variant="text"
                      onClick={handleBack}
                      data-qe-id="MoveTimeDialogBackButton"
                      disabled={isSubmitting}
                    >
                      <FormattedMessage id="button.back" />
                    </Button>
                  </>
                ) : (
                  cancelButton
                )}
                <LoadingButton
                  isLoading={isSubmitting}
                  onClick={handleNext}
                  color="primary"
                  variant="contained"
                  disabled={isNextDisabled}
                  data-qe-id="MoveTimeDialogNextButton"
                >
                  <FormattedMessage
                    id={
                      activeStep === MOVE_TIME_STEP_INDICES.DESTINATION
                        ? 'moveTimeDialog.moveTime'
                        : 'button.next'
                    }
                  />
                </LoadingButton>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

MoveTimeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceProject: PropTypes.object,
  sourceTask: PropTypes.object
};

export default MoveTimeDialog;
